import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { REDIRECT_PAGE_PATH } from 'routes/paths';

export interface InternalRedirectOptions {
  /** The path to redirect to */
  to: string;
  /** The delay in milliseconds before redirecting (optional, default: 0) */
  delay?: number;
  /** Whether to show a loading spinner during the delay (optional, default: false) */
  showLoading?: boolean;
}

/**
 * Hook to navigate to the internal redirect page with specified options.
 *
 * @example
 * const redirect = useInternalRedirect();
 *
 * // Immediate redirect
 * redirect({ to: '/welcome' });
 *
 * // Redirect after 2 seconds
 * redirect({ to: '/dashboard', delay: 2000 });
 *
 * // Redirect after 3 seconds with loading spinner
 * redirect({ to: '/devices', delay: 3000, showLoading: true });
 */
export default function useInternalRedirect() {
  const navigate = useNavigate();

  const redirect = useCallback(
    ({ to, delay, showLoading }: InternalRedirectOptions) => {
      const params = new URLSearchParams({ to });

      if (delay !== undefined && delay > 0) {
        params.append('delay', delay.toString());
      }

      if (showLoading) {
        params.append('showLoading', 'true');
      }

      navigate(`${REDIRECT_PAGE_PATH.original}?${params.toString()}`);
    },
    [navigate]
  );

  return redirect;
}
